<template>
  <c-leaf class="checkout-summary">
    <h6><slot name="title"/></h6>

    <ul class="summary-list" v-if="!is.initial">
      <component
        v-for="(cost, cIndex) in summary"
        :key="cIndex"
        :is="cost.component"
        :text="cost.label"
        :amount="cost.price"
        v-bind="cost.attrs"
      />
    </ul>

    <loader v-if="is.initial" variant="primary"/>
    <slot v-else name="content"/>
  </c-leaf>
</template>

<script>
import { COMPONENT_BASKET_MODULES, COMPONENT_CHECKOUTSUMMARY_COMPONENT_TYPE_MAP, COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP } from '@/constants'

import Loader from '@/components/Loader'
import CheckoutSummaryPrice from '@/components/authenticated/Checkout/CheckoutSummaryPrice'
import CheckoutSummarySeparator from '@/components/authenticated/Checkout/CheckoutSummarySeparator'

const BASKET_MODULES = Object.values(COMPONENT_BASKET_MODULES)
const DEFAULT_MODULENAME = (BASKET_MODULES.find(m => m.isDefault) || BASKET_MODULES[0]).name
const ALLOWED_MODULE_NAMES = BASKET_MODULES.map(m => m.name)

export default {
  name: 'CheckoutSummary',
  components: {
    Loader,
    CheckoutSummaryPrice,
    CheckoutSummarySeparator
  },
  props: {
    moduleName: {
      type: String,
      default: DEFAULT_MODULENAME,
      validator: value => ALLOWED_MODULE_NAMES.includes(value)
    }
  },
  computed: {
    isDefaultModule () {
      return this.moduleName === DEFAULT_MODULENAME
    },
    module () {
      return BASKET_MODULES.find(m => m.name === this.moduleName)
    },
    is () {
      return this.$store.getters[`${this.module.name}/is`]
    },
    basket () {
      return this.$store.getters[`${this.module.name}/${this.module.getters.basket}`]
    },
    summary () {
      const priceDetails = this.basket.priceDetail || {}

      const mapLineTypeToType = (lineType) => {
        if (!lineType) return null
        const typeMap = {
          'Separator': 'SeparatorCosts',
          'ProductCost': 'ProductCosts',
          'NetCost': 'ProductCosts',
          'DeliveryCost': 'AdditionalCosts',
          'VatCost': 'VatCosts',
          'TotalCost': 'TotalCosts'
        }
        return typeMap[lineType]
      }

      const priceLines = priceDetails.priceLines || []
      
      // Check if TotalCost already exists in priceLines
      const hasTotalCost = priceLines.some(p => {
        const type = mapLineTypeToType(p.lineType)
        return type === COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.total.type
      })

      // Only add manual total if it doesn't already exist
      const linesToProcess = hasTotalCost 
        ? priceLines 
        : priceLines.concat({
            lineType: 'TotalCost',
            label: priceDetails.totalPriceLabel || {},
            price: priceDetails.totalPrice
          })

      let hasAddedSubtotalSeparator = false

      return linesToProcess
        .map(p => {
          const type = mapLineTypeToType(p.lineType)
          const typeMapping = COMPONENT_CHECKOUTSUMMARY_COMPONENT_TYPE_MAP[type]
          
          // Skip if type mapping doesn't exist
          if (!typeMapping) {
            return null
          }

          return Object.assign({}, p, {
            type: type,
            component: typeMapping.component,
            attrs: typeMapping.attrs || {},
            label: (p.label && typeof p.label === 'object' ? p.label[this.$store.getters['gui/getLanguage']] : p.label) || ''
          })
        })
        .filter(p => p !== null)
        .reduce((summary, p) => {
          // Only add separator before the first ProductCost (subtotal), not before NetCost
          if (p.type === COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.subtotal.type && 
              p.lineType === 'ProductCost' && 
              !hasAddedSubtotalSeparator) {
            hasAddedSubtotalSeparator = true
            return summary.concat([COMPONENT_CHECKOUTSUMMARY_TYPE_COMPONENT_MAP.separator, p])
          }
          return summary.concat(p)
        }, [])
    }
  }
}
</script>

<style lang="scss">
.checkout-summary {
  .summary-list {
    @include list-unstyled();

    > li {}
  }
}
</style>
